/**
 * useAuthUrlCleanup.js - Hook for automatic Auth0 URL cleanup
 *
 * This hook automatically cleans Auth0 callback parameters from URLs
 * to prevent "Invalid state" errors on page refreshes.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAuth0CallbackParams, cleanAuth0CallbackParams } from '../utils/AuthUrlCleanup';

/**
 * Hook that automatically cleans Auth0 callback parameters from URLs
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.cleanOnMount - Clean parameters on component mount (default: true)
 * @param {boolean} options.cleanOnRouteChange - Clean parameters on route changes (default: true)
 * @param {number} options.delay - Delay in ms before cleaning (default: 100)
 */
export function useAuthUrlCleanup(options = {}) {
  const { cleanOnMount = true, cleanOnRouteChange = true, delay = 100 } = options;

  const location = useLocation();

  // Clean on mount
  useEffect(() => {
    if (!cleanOnMount) return;

    const timeoutId = setTimeout(() => {
      if (hasAuth0CallbackParams()) {
        cleanAuth0CallbackParams();
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [cleanOnMount, delay]);

  // Clean on route changes
  useEffect(() => {
    if (!cleanOnRouteChange) return;

    const timeoutId = setTimeout(() => {
      if (hasAuth0CallbackParams()) {
        cleanAuth0CallbackParams();
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, cleanOnRouteChange, delay]);

  return {
    hasCallbackParams: hasAuth0CallbackParams(),
    cleanNow: () => cleanAuth0CallbackParams(),
  };
}

export default useAuthUrlCleanup;
